import { useMemo } from 'react';
import JsonLd from '../../components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://workplaysave.com';
const DEFAULT_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/7965406aa664502cb6a3888418e2a788.jpg';

export default function AffiliateDisclosurePage() {
  const webPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Affiliate Disclosure | Work Play Save',
    description: 'Learn how Work Play Save earns commissions through affiliate links and how this supports our reviews at no extra cost to you.',
    url: `${SITE_URL}/affiliate-disclosure`,
    publisher: {
      '@type': 'Organization',
      name: 'Work Play Save',
      url: SITE_URL,
    },
    dateModified: '2026-03-18',
  }), []);

  return (
    <>
      <title>Affiliate Disclosure | Work Play Save</title>
      <meta name="description" content="Learn how Work Play Save earns commissions through affiliate links and how this supports our reviews at no extra cost to you." />
      <meta name="keywords" content="affiliate disclosure, affiliate links, commission disclosure, affiliate marketing policy, sponsored links, product recommendations disclosure" />
      <meta name="robots" content="index, follow" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Affiliate Disclosure | Work Play Save" />
      <meta property="og:description" content="Learn how Work Play Save earns commissions through affiliate links and how this supports our reviews at no extra cost to you." />
      <meta property="og:url" content={`${SITE_URL}/affiliate-disclosure`} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:site_name" content="Work Play Save" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Affiliate Disclosure | Work Play Save" />
      <meta name="twitter:description" content="Learn how Work Play Save earns commissions through affiliate links and how this supports our reviews at no extra cost to you." />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      <link rel="canonical" href={`${SITE_URL}/affiliate-disclosure`} />
      <JsonLd data={webPageSchema} />
      <div className="min-h-screen">
        {/* Header */}
        <header className="bg-gradient-to-b from-gray-900 to-black border-b border-gray-800">
          <div className="max-w-4xl mx-auto px-6 py-16">
            <div className="text-center space-y-4">
              <h1 className="text-5xl font-bold text-white uppercase tracking-tight">
                Affiliate Disclosure
                <div className="h-1 w-32 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto mt-4"></div>
              </h1>
              <p className="text-gray-400">Transparency in our recommendations</p>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="space-y-8 text-gray-300 leading-relaxed">
            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Our Commitment to Transparency</h2>
              <p className="text-lg">
                Work Play Save is committed to providing honest, unbiased product reviews and recommendations. This page explains how we earn revenue and how it may affect the content you see on our site.
              </p>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Affiliate Relationships</h2>
              <p className="text-lg">
                Work Play Save participates in affiliate marketing programs. This means that when you click on certain links on our site and make a purchase, we may earn a commission at no additional cost to you.
              </p>
              <p className="text-lg">
                We may have affiliate relationships with retailers including (but not limited to):
              </p>
              <ul className="list-disc list-inside space-y-2 text-lg ml-4">
                <li>Amazon Associates</li>
                <li>Other major electronics and tech retailers</li>
                <li>Product manufacturers and brands</li>
              </ul>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">How Affiliate Links Work</h2>
              <p className="text-lg">
                When you click an affiliate link and make a purchase, the retailer pays us a small commission. This commission comes from the retailer's marketing budget and does not increase the price you pay.
              </p>
              <p className="text-lg">
                These commissions help support the site and allow us to continue creating free content, conducting product research, and publishing reviews.
              </p>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Our Editorial Independence</h2>
              <p className="text-lg">
                Our affiliate relationships do not influence our editorial content or product recommendations. We maintain full editorial independence and only recommend products we believe offer genuine value to our readers.
              </p>
              <p className="text-lg">
                Key principles we follow:
              </p>
              <ul className="list-disc list-inside space-y-2 text-lg ml-4">
                <li>We research products thoroughly before recommending them</li>
                <li>We consider user reviews, expert opinions, and real-world performance</li>
                <li>We do not accept payment for positive reviews</li>
                <li>We disclose when content contains affiliate links</li>
                <li>We prioritize reader value over commission potential</li>
              </ul>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Product Reviews and Recommendations</h2>
              <p className="text-lg">
                Our product reviews and buying guides are based on:
              </p>
              <ul className="list-disc list-inside space-y-2 text-lg ml-4">
                <li>Product specifications and features</li>
                <li>User reviews and feedback from verified buyers</li>
                <li>Expert opinions and professional reviews</li>
                <li>Price comparisons and value analysis</li>
                <li>Real-world use cases and performance testing when possible</li>
              </ul>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Your Support Matters</h2>
              <p className="text-lg">
                When you make a purchase through our affiliate links, you're directly supporting Work Play Save. This allows us to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-lg ml-4">
                <li>Continue publishing free content and reviews</li>
                <li>Research and compare more products</li>
                <li>Improve the site and user experience</li>
                <li>Maintain editorial independence</li>
              </ul>
              <p className="text-lg">
                We appreciate your support and trust in our recommendations.
              </p>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">Questions or Concerns</h2>
              <p className="text-lg">
                If you have questions about our affiliate relationships or how we select products to recommend, please contact us through our website. We're committed to transparency and welcome your feedback.
              </p>
            </section>

            <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent my-12"></div>

            <section className="space-y-4">
              <h2 className="text-3xl font-bold text-white">FTC Compliance</h2>
              <p className="text-lg">
                This disclosure is in accordance with the Federal Trade Commission's 16 CFR, Part 255: "Guides Concerning the Use of Endorsements and Testimonials in Advertising."
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}